// Orenza data table — list view used by every master page.
// Tally-style keyboard nav: PgUp/PgDn scroll rows; Up/Down move highlight; Enter opens row;
// Insert key creates a new record; Delete soft-deletes the highlighted row (with confirm).
// Search box (Ctrl+F) auto-focuses.

import { useEffect, useMemo, useRef, useState, type ReactNode, type KeyboardEvent } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { cn } from '@/lib/cn';
import { KbdChip } from '@/components/primitives/KbdChip';
import { Button } from '@/components/primitives/Button';
import { Input } from '@/components/primitives/Input';
import { ConfirmModal } from '@/components/primitives/ConfirmModal';
import { pluralize } from '@/utils/format';

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  width?: string;          // e.g. '180px', '20%', 'minmax(120px,1fr)'
  align?: 'left' | 'right' | 'center';
  numeric?: boolean;       // applies tabular mono via .num class
  sortable?: boolean;
}

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface DataTableProps<T> {
  title: string;
  subtitle?: string;
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  pagination?: Pagination;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  search?: string;
  onSearchChange?: (q: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRowClick?: (row: T) => void;
  onCreate?: () => void;     // triggered by toolbar button + Insert key
  onEdit?: (row: T) => void; // triggered by Enter / double-click
  onDelete?: (row: T) => void; // triggered by Delete key (with confirm)
  createLabel?: string;       // toolbar button label (defaults to "New")
  emptyMessage?: string;
  rightToolbar?: ReactNode;   // additional toolbar buttons
  underToolbar?: ReactNode;   // optional second row below the toolbar (Phase 5.8 — filter strip)
  hotkeysEnabled?: boolean;   // pages set false while a FormDrawer is open
  // When Del is pressed on a row this returns true for, the confirm reads
  // "Restore" and onDelete is expected to restore it. Default: soft-deleted
  // rows only. Pages whose delete = deactivate (isActive models) override.
  isRestorable?: (row: T) => boolean;
}

export function DataTable<T>({
  title,
  subtitle,
  columns,
  rows,
  rowKey,
  pagination,
  onPageChange,
  onPageSizeChange,
  search = '',
  onSearchChange,
  isLoading,
  error,
  onRowClick,
  onCreate,
  onEdit,
  onDelete,
  createLabel = 'New',
  emptyMessage = 'No records yet.',
  rightToolbar,
  underToolbar,
  hotkeysEnabled = true,
  isRestorable,
}: DataTableProps<T>) {
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [confirmRow, setConfirmRow] = useState<T | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Debounced search: the box stays instant (local state) but the parent —
  // whose value feeds straight into a TanStack query key — hears about it
  // 300ms after the last keystroke. Without this every character fired a
  // findMany + COUNT with triple ILIKE against the server.
  const [searchDraft, setSearchDraft] = useState(search);
  useEffect(() => { setSearchDraft(search); }, [search]);
  useEffect(() => {
    if (searchDraft === search) return;
    const t = setTimeout(() => onSearchChange?.(searchDraft), 300);
    return () => clearTimeout(t);
  }, [searchDraft, search, onSearchChange]);

  // Delete on a restorable row means "bring it back" — the confirm copy must
  // say so, not threaten a second deletion. Default: soft-deleted rows only
  // (isActive=false is an editable flag, not deletion, unless the page — whose
  // delete IS deactivate — opts in via the isRestorable prop).
  const rowIsRestorable = isRestorable ?? ((row: T) => Boolean((row as { deletedAt?: string | null }).deletedAt));
  const confirmIsRestore = confirmRow != null && rowIsRestorable(confirmRow);

  // Clamp the highlight index when rows change
  useEffect(() => {
    if (highlightIndex >= rows.length) setHighlightIndex(Math.max(0, rows.length - 1));
  }, [rows.length, highlightIndex]);

  // Keyboard navigation — scoped to the table container so it doesn't fire while typing in a form
  useHotkeys(
    'up',
    (e) => { e.preventDefault(); setHighlightIndex((i) => Math.max(0, i - 1)); },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'down',
    (e) => { e.preventDefault(); setHighlightIndex((i) => Math.min(rows.length - 1, i + 1)); },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'pageup',
    (e) => { e.preventDefault(); if (pagination && pagination.page > 1) onPageChange?.(pagination.page - 1); },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'pagedown',
    (e) => { e.preventDefault(); if (pagination && pagination.page < pagination.totalPages) onPageChange?.(pagination.page + 1); },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'enter',
    (e) => {
      if (rows[highlightIndex] && onEdit) {
        e.preventDefault();
        onEdit(rows[highlightIndex]);
      }
    },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'insert',
    (e) => { if (onCreate) { e.preventDefault(); onCreate(); } },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'delete',
    (e) => {
      const row = rows[highlightIndex];
      if (row && onDelete) {
        e.preventDefault();
        setConfirmRow(row);
      }
    },
    { enableOnFormTags: false, enabled: hotkeysEnabled },
  );
  useHotkeys(
    'ctrl+f',
    (e) => { e.preventDefault(); searchRef.current?.focus(); },
    { enableOnFormTags: ['input', 'textarea'], enabled: hotkeysEnabled },
  );

  const gridTemplate = useMemo(
    () => columns.map((c) => c.width ?? 'minmax(120px, 1fr)').join(' '),
    [columns],
  );

  function onRowKey(e: KeyboardEvent<HTMLDivElement>, row: T) {
    if (e.key === 'Enter') {
      e.preventDefault();
      (onEdit ?? onRowClick)?.(row);
    }
  }

  return (
    <div ref={containerRef} className="flex h-full flex-col bg-snow">
      {/* Header — thin duo-tone accent bar + whisper wash give every master list
          the same section identity as the Gateway/BI surfaces. Token-only. */}
      <header className="sticky top-0 z-10 border-b border-mist bg-snow/95 backdrop-blur">
        <div className="h-[2px]" style={{ background: 'var(--accent-bar)' }} aria-hidden />
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-3 px-6 py-4" style={{ backgroundImage: 'var(--wash-card)' }}>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <h1 className="font-display text-display-md font-semibold text-obsidian" style={{ letterSpacing: 'var(--tracking-display)' }}>
                {title}
              </h1>
            </div>
            {subtitle && <p className="text-body-sm text-slate">{subtitle}</p>}
          </div>

          <div className="ml-auto flex min-w-0 flex-wrap items-center justify-end gap-3">
            <div className="w-full sm:w-[280px]">
              <Input
                ref={searchRef}
                type="search"
                placeholder="Search…"
                value={searchDraft}
                onChange={(e) => setSearchDraft(e.target.value)}
                rightSlot={<KbdChip className="hidden sm:inline-flex">Ctrl F</KbdChip>}
              />
            </div>
            {rightToolbar}
            {onCreate && (
              <Button onClick={onCreate} leftIcon={<span className="text-lg leading-none">+</span>} className="whitespace-nowrap">
                {createLabel}
                <span className="ml-2 hidden xl:inline-flex"><KbdChip className="bg-cobalt/20 text-snow border-cobalt/40">Ins</KbdChip></span>
              </Button>
            )}
          </div>
        </div>
        {underToolbar}
      </header>

      {/* Table */}
      <main className="mx-auto flex-1 w-full max-w-[1400px] overflow-auto px-6 py-4">
        {error && (
          <div className="mb-4 rounded-md border border-crimson/20 bg-crimson/[0.05] px-3 py-2 text-body-sm text-crimson">
            {error}
          </div>
        )}

        <div className="overflow-hidden rounded-md border border-mist bg-haze">
          {/* Horizontal scroll wrapper — mobile (<md) lets the grid scroll instead of clipping. */}
          <div className="overflow-x-auto">
            {/* Column headers */}
            <div
              // v1.7.0: haze, not frost. With the canvas pure white, a white header
              // row on a white table had nothing but its bottom border — the column
              // labels floated. The well tone puts them on a surface again.
              className="grid min-w-max gap-x-4 border-b border-mist bg-haze px-4 py-2 text-micro font-medium uppercase tracking-[0.12em] text-obsidian"
              style={{ gridTemplateColumns: gridTemplate }}
            >
              {columns.map((c) => (
                <div
                  key={c.key}
                  className={cn(
                    'truncate',
                    c.align === 'right' && 'text-right',
                    c.align === 'center' && 'text-center',
                  )}
                >
                  {c.header}
                </div>
              ))}
            </div>

            {/* Rows */}
            {isLoading ? (
              <div className="flex items-center justify-center py-16 text-body-sm text-cool">
                Loading…
              </div>
            ) : rows.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
                <p className="text-body-md text-slate">{emptyMessage}</p>
                {onCreate && (
                  <button
                    onClick={onCreate}
                    className="text-body-sm text-cobalt hover:underline"
                  >
                    Create your first record →
                  </button>
                )}
              </div>
            ) : (
              rows.map((row, idx) => {
                const k = rowKey(row);
                const highlighted = idx === highlightIndex;
                return (
                  <div
                    key={k}
                    tabIndex={highlighted ? 0 : -1}
                    role="row"
                    onClick={() => { setHighlightIndex(idx); onRowClick?.(row) ?? onEdit?.(row); }}
                    onKeyDown={(e) => onRowKey(e, row)}
                    className={cn(
                      'grid min-w-max cursor-pointer gap-x-4 border-b border-mist px-4 py-2 text-body-sm text-obsidian transition-colors',
                      'hover:bg-cobalt/[0.05]',
                      highlighted && 'bg-cobalt/[0.05] outline-none',
                    )}
                    style={{ gridTemplateColumns: gridTemplate }}
                  >
                    {columns.map((c) => (
                      <div
                        key={c.key}
                        className={cn(
                          'truncate',
                          c.numeric && 'font-mono tabular-nums',
                          c.align === 'right' && 'text-right',
                          c.align === 'center' && 'text-center',
                        )}
                      >
                        {c.render(row)}
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </main>

      {/* Footer: pagination + kbd hints */}
      <footer className="border-t border-mist bg-snow/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-5 gap-y-2 px-6 py-3 text-caption text-slate">
          <span className="flex items-center gap-2">
            <KbdChip>↑↓</KbdChip> Navigate
          </span>
          <span className="flex items-center gap-2">
            <KbdChip>Enter</KbdChip> Open
          </span>
          {onCreate && (
            <span className="flex items-center gap-2">
              <KbdChip>Ins</KbdChip> New
            </span>
          )}
          {onDelete && (
            <span className="flex items-center gap-2">
              <KbdChip>Del</KbdChip> Delete / Restore
            </span>
          )}
          {pagination && (
            <span className="flex items-center gap-2">
              <KbdChip>PgUp/PgDn</KbdChip> Page
            </span>
          )}

          {pagination && (
            <div className="ml-auto flex items-center gap-3">
              <span className="font-mono tabular-nums">
                Page {pagination.page} of {pagination.totalPages} · {pluralize(pagination.total, 'record')}
              </span>
              <button
                onClick={() => onPageChange?.(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-caption text-obsidian hover:bg-haze disabled:cursor-not-allowed disabled:text-cool"
              >
                ← Prev
              </button>
              <button
                onClick={() => onPageChange?.(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages}
                className="rounded-sm border border-mist bg-haze px-2 py-1 text-caption text-obsidian hover:bg-haze disabled:cursor-not-allowed disabled:text-cool"
              >
                Next →
              </button>
              {onPageSizeChange && (
                <select
                  value={pagination.pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="rounded-sm border border-mist bg-haze px-2 py-1 text-caption text-obsidian"
                >
                  {[25, 50, 100, 200].map((s) => (
                    <option key={s} value={s}>{s} / page</option>
                  ))}
                </select>
              )}
            </div>
          )}
        </div>
      </footer>

      <ConfirmModal
        open={confirmRow != null}
        title={confirmIsRestore ? 'Restore this record?' : 'Delete this record?'}
        body={confirmIsRestore
          ? 'It comes back active and usable everywhere immediately.'
          : 'It moves out of lists but is never destroyed — tick "Show deleted" later and press Del on it to restore.'}
        confirmLabel={confirmIsRestore ? 'Restore' : 'Delete'}
        tone={confirmIsRestore ? 'primary' : 'danger'}
        onConfirm={() => { if (confirmRow) onDelete?.(confirmRow); setConfirmRow(null); }}
        onCancel={() => setConfirmRow(null)}
      />
    </div>
  );
}
